/**
 * Driving-safe settings: the four adjustments someone may reasonably need
 * without leaving navigation, plus a direct route to the complete settings
 * page. Every quick choice is one tap and applies at once.
 */

import { t } from '../core/i18n';
import type { MapTheme, Settings } from '../core/settings';

type PatchSettings = (patch: Partial<Settings>) => void;

interface Choice {
  value: string;
  label: string;
  selected: boolean;
}

export interface QuickAction {
  id: string;
  label: string;
  icon: string;
  run: () => void;
  visible?: () => boolean;
}

export class QuickSettings {
  readonly button: HTMLButtonElement;
  private readonly panel: HTMLElement;
  private actions: QuickAction[] = [];

  constructor(
    parent: HTMLElement,
    private readonly getSettings: () => Settings,
    private readonly patchSettings: PatchSettings,
    private readonly openMainSettings: () => void = () => {},
  ) {
    this.button = document.createElement('button');
    this.button.className = 'quick-settings-fab';
    this.button.hidden = true;
    this.button.setAttribute('aria-label', t('quick.title'));
    this.button.setAttribute('aria-expanded', 'false');
    this.button.innerHTML = `
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M9.7 3.4 10.2 2h3.6l.5 1.4 1.5.6 1.3-.6 2.5 2.5-.6 1.3.6 1.5 1.4.5v3.6l-1.4.5-.6 1.5.6 1.3-2.5 2.5-1.3-.6-1.5.6-.5 1.4h-3.6l-.5-1.4-1.5-.6-1.3.6-2.5-2.5.6-1.3-.6-1.5-1.4-.5V9.2l1.4-.5.6-1.5-.6-1.3 2.5-2.5 1.3.6 1.5-.6Z"
              fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/>
        <circle cx="12" cy="11" r="3.1" fill="none" stroke="currentColor" stroke-width="1.8"/>
      </svg>`;
    parent.appendChild(this.button);

    this.panel = document.createElement('div');
    this.panel.className = 'quick-settings';
    this.panel.hidden = true;
    parent.appendChild(this.panel);

    this.button.addEventListener('click', () => this.open());
    this.panel.addEventListener('click', (event) => this.handleClick(event));
    this.panel.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      this.close();
    });
  }

  setVisible(visible: boolean): void {
    this.button.hidden = !visible;
    if (!visible) this.close();
  }

  /** Secondary mobile tools collected behind this one map button. */
  setActions(actions: QuickAction[]): void {
    this.actions = actions;
    if (this.isOpen()) this.render();
  }

  isOpen(): boolean {
    return !this.panel.hidden;
  }

  open(): void {
    this.render();
    this.panel.hidden = false;
    this.button.setAttribute('aria-expanded', 'true');
    window.requestAnimationFrame(() =>
      this.panel.querySelector<HTMLButtonElement>('[data-main-settings]')?.focus(),
    );
  }

  close(restoreFocus = true): void {
    const wasOpen = !this.panel.hidden;
    this.panel.hidden = true;
    this.button.setAttribute('aria-expanded', 'false');
    if (restoreFocus && wasOpen && !this.button.hidden) this.button.focus();
  }

  private handleClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (target === this.panel || target.closest('[data-quick-close]')) {
      this.close();
      return;
    }

    if (target.closest('[data-main-settings]')) {
      this.close(false);
      this.openMainSettings();
      return;
    }

    const choice = target.closest<HTMLButtonElement>('[data-quick-setting]');
    if (!choice) {
      const action = target.closest<HTMLButtonElement>('[data-quick-action]');
      const picked = this.actions.find((item) => item.id === action?.dataset.quickAction);
      if (!picked || (picked.visible && !picked.visible())) return;
      this.close();
      picked.run();
      return;
    }
    const value = choice.dataset.value!;
    switch (choice.dataset.quickSetting) {
      case 'zoom':
        this.patchSettings({ driveZoom: Number(value) });
        break;
      case 'voice':
        // Choosing a voice level is also an explicit request to hear guidance.
        this.patchSettings({ soundMode: 'voice', voiceVolume: Number(value) });
        break;
      case 'angle':
        this.patchSettings({ cameraPitch: Number(value) as Settings['cameraPitch'] });
        break;
      case 'theme':
        this.patchSettings({ theme: value as MapTheme });
        break;
    }
    this.render();
  }

  private render(): void {
    const s = this.getSettings();
    const visibleActions = this.actions.filter((action) => !action.visible || action.visible());
    const zooms: Choice[] = [
      { value: '0', label: t('quick.auto'), selected: s.driveZoom === 0 },
      { value: '14.5', label: t('quick.wide'), selected: s.driveZoom === 14.5 },
      { value: '15.5', label: t('quick.normal'), selected: s.driveZoom === 15.5 },
      { value: '16.5', label: t('quick.near'), selected: s.driveZoom === 16.5 },
    ];
    const voices: Choice[] = [
      { value: '0.45', label: t('quick.low'), selected: s.soundMode === 'voice' && s.voiceVolume === 0.45 },
      { value: '0.75', label: t('quick.medium'), selected: s.soundMode === 'voice' && s.voiceVolume === 0.75 },
      { value: '1', label: t('quick.high'), selected: s.soundMode === 'voice' && s.voiceVolume === 1 },
    ];
    const angles: Choice[] = [
      { value: '0', label: t('quick.flat'), selected: s.cameraPitch === 0 },
      { value: '30', label: t('quick.tilt'), selected: s.cameraPitch === 30 },
      { value: '60', label: t('quick.3d'), selected: s.cameraPitch === 60 },
    ];
    const themes: Choice[] = [
      { value: 'auto', label: t('quick.auto'), selected: s.theme === 'auto' },
      { value: 'day', label: t('set.theme.day'), selected: s.theme === 'day' },
      { value: 'night', label: t('set.theme.night'), selected: s.theme === 'night' },
    ];

    this.panel.innerHTML = `
      <section class="quick-settings__card" role="dialog" aria-modal="true" aria-label="${t('quick.title')}">
        <header class="quick-settings__head">
          <span class="quick-settings__gear">${this.button.innerHTML}</span>
          <h2>${t('quick.title')}</h2>
          <button class="icon-btn" data-quick-close aria-label="${t('preview.cancel')}">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6 18 18M18 6 6 18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg>
          </button>
        </header>
        <button type="button" class="quick-settings__main" data-main-settings>
          <span class="quick-settings__main-icon" aria-hidden="true">${this.button.innerHTML}</span>
          <span class="quick-settings__main-copy">
            <strong>${t('quick.allSettings')}</strong>
            <small>${t('quick.allSettingsHint')}</small>
          </span>
          <svg class="quick-settings__main-chevron" viewBox="0 0 24 24" aria-hidden="true">
            <path d="m9 6 6 6-6 6" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        ${
          visibleActions.length
            ? `<nav class="quick-settings__tools" aria-label="${t('quick.tools')}">
                ${visibleActions
                  .map(
                    (action) => `<button type="button" class="quick-settings__tool" data-quick-action="${action.id}">
                      <span class="quick-settings__tool-icon">${action.icon}</span>
                      <span>${action.label}</span>
                    </button>`,
                  )
                  .join('')}
              </nav>`
            : ''
        }
        <div class="quick-settings__body">
          ${group('zoom', t('set.driveZoom'), zooms)}
          ${group('voice', t('sound.volume'), voices)}
          ${group('angle', t('set.pitch'), angles)}
          ${group('theme', t('set.theme'), themes)}
        </div>
      </section>`;
  }
}

function group(setting: string, label: string, choices: Choice[]): string {
  return `<fieldset class="quick-settings__group">
    <legend>${label}</legend>
    <div class="quick-settings__choices quick-settings__choices--${choices.length}">
      ${choices
        .map(
          (choice) => `<button type="button" data-quick-setting="${setting}" data-value="${choice.value}"
            aria-pressed="${choice.selected}" class="${choice.selected ? 'is-on' : ''}">${choice.label}</button>`,
        )
        .join('')}
    </div>
  </fieldset>`;
}
